import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { catchError, finalize, shareReplay, tap } from "rxjs/operators";

import { ApiService } from "@core/auth/services/api.service";
import { AuthService } from "@core/auth/services/auth.service";
import { ApiResponse } from "@core/auth/models/api-response.model";
import { CurrentUserData } from "@core/auth/models/auth.model"

@Injectable({
    providedIn: "root",
})
export class UserService {
    private readonly currentUserSubject = new BehaviorSubject<CurrentUserData | null>(null);
    readonly currentUser$ = this.currentUserSubject.asObservable();

    private cachedResponse: ApiResponse<CurrentUserData> | null = null;
    private currentUserRequest$: Observable<ApiResponse<CurrentUserData>> | null = null;
    private cachedAccountKey: string | null = null;

    constructor(
        private readonly api: ApiService,
        private readonly auth: AuthService,
    ) {}

    getCurrentUser(forceRefresh = false): Observable<ApiResponse<CurrentUserData>> {
        this.resetCacheIfAccountChanged();
        
        if (forceRefresh) {
            this.clearCurrentUserCache();
        }
        
        if (this.cachedResponse) {
            console.debug("[PERF][USER] /api/me cache hit");
            return of(this.cachedResponse);
        }
        
        if (this.currentUserRequest$) {
            console.debug("[PERF][USER] /api/me joined in-flight request");
            return this.currentUserRequest$;
        }
        
        const startedAt = performance.now();
        const accountKey = this.getAccountKey();
        
        this.currentUserRequest$ = this.api.get<ApiResponse<CurrentUserData>>("me").pipe(
            tap((response) => {
                this.cachedResponse = response;
                this.cachedAccountKey = accountKey;
                this.currentUserSubject.next(response.data ?? null);
                console.debug(`[PERF][USER] /api/me resolved in ${Math.round(performance.now() - startedAt)}ms`);
        }),
        catchError((error: unknown) => {
            this.currentUserSubject.next(null);
            console.debug(`[PERF][USER] /api/me failed in ${Math.round(performance.now() - startedAt)}ms`);
            return throwError(() => error);
        }),
        finalize(() => {
            this.currentUserRequest$ = null;
        }),
            shareReplay(1),
        );

        return this.currentUserRequest$;
    }

    getCurrentUserSnapshot(): CurrentUserData | null {
        this.resetCacheIfAccountChanged();
        return this.currentUserSubject.value;
    }

    clearCurrentUserCache(): void {
        this.cachedResponse = null;
        this.currentUserRequest$ = null;
        this.cachedAccountKey = null;
        this.currentUserSubject.next(null);
    }

    private resetCacheIfAccountChanged(): void {
        const accountKey = this.getAccountKey();
        if (accountKey === this.cachedAccountKey) {
        return;
        }

        this.clearCurrentUserCache();
    }

    private getAccountKey(): string | null {
        const account = this.auth.getActiveAccount();
        return account?.homeAccountId ?? account?.localAccountId ?? account?.username ?? null;
    }
}